import { property, state } from 'lit/decorators.js';
import {LitElement} from 'lit';
import {SbkInput} from "@/components/input/input";
import {SbkCheckbox} from "@/components/checkbox/checkbox";
import {SbkRadio} from "@/components/radio/radio";
import {SbkRadioGroup} from "@/components/radio-group/radio-group";
import {SbkSelect} from "@/components/select/select";
import {SbkDatepicker} from "@/components/datepicker/datepicker";
import {SbkTextarea} from "@/components/textarea/textarea";

type Constructor<T> =
// eslint-disable-next-line @typescript-eslint/no-explicit-any
    new (...args: any[]) => T;

export declare class FormControlDependencyMixinInterface {
    fieldDependency: string;
    fieldDependencyValue: string;
    fieldDependencyAction: 'show' | 'required';
    fieldDependencyComparisonType: string
    isFieldDependencyMatching: boolean;

    _show(): void;
    _hide(): void;
}

export const FormControlDependencyMixin = <T extends Constructor<LitElement>>(superClass: T) => {
    class FormControlDependencyMixin extends superClass {

        @property({ attribute: 'field-dependency' })
        fieldDependency: string = ''

        @property({ attribute: 'field-dependency-value' })
        fieldDependencyValue: string = ''

        @property({ attribute: 'field-dependency-action' })
        fieldDependencyAction: 'show' | 'required' | 'hide' = 'show'

        @property({ attribute: 'field-dependency-comparison-type' })
        fieldDependencyComparisonType: string = ''

        @state()
        isFieldDependencyMatching: boolean = false

         connectedCallback() {
            super.connectedCallback();
            if (this.fieldDependency) {
                const fieldDependencyElements = this.closest('form')?.querySelectorAll(`[name$="${this.fieldDependency}"], [id='${this.fieldDependency}']`);
                if (fieldDependencyElements) {
                    fieldDependencyElements.forEach(async fieldDependencyElement =>  {
                        await (<LitElement> fieldDependencyElement).updateComplete;
                        this._updateVisibility(fieldDependencyElement, this._getFieldDependencyElementValues(fieldDependencyElement))
                        fieldDependencyElement.addEventListener('input-change', this._handleFieldDependency);
                        fieldDependencyElement.addEventListener('textarea-change', this._handleFieldDependency);
                        fieldDependencyElement.addEventListener('radio-group-change', this._handleFieldDependency);
                        fieldDependencyElement.addEventListener('checkbox-change', this._handleFieldDependency);
                        fieldDependencyElement.addEventListener('select-value-changed', this._handleFieldDependency);
                        fieldDependencyElement.addEventListener('form-control-dependency-change', this._handleFieldDependency);
                        fieldDependencyElement.addEventListener('datepicker-change', this._handleFieldDependency);
                    });
                }
            }
        }

        disconnectedCallback() {
            if (this.fieldDependency) {
                const fieldDependencyElements = this.closest('form')?.querySelectorAll(`[name$="${this.fieldDependency}"], [id='${this.fieldDependency}']`);
                if (fieldDependencyElements) {
                    fieldDependencyElements.forEach(async fieldDependencyElement => {
                        fieldDependencyElement && fieldDependencyElement.removeEventListener('input-change', this._handleFieldDependency);
                        fieldDependencyElement && fieldDependencyElement.removeEventListener('textarea-change', this._handleFieldDependency);
                        fieldDependencyElement && fieldDependencyElement.removeEventListener('radio-group-change', this._handleFieldDependency);
                        fieldDependencyElement && fieldDependencyElement.removeEventListener('checkbox-change', this._handleFieldDependency);
                        fieldDependencyElement && fieldDependencyElement.removeEventListener('select-value-changed', this._handleFieldDependency);
                        fieldDependencyElement && fieldDependencyElement.removeEventListener('form-control-dependency-change', this._handleFieldDependency);
                        fieldDependencyElement && fieldDependencyElement.removeEventListener('datepicker-change', this._handleFieldDependency);
                    });
                }
            }
            super.disconnectedCallback();
        }

        private _handleFieldDependency = (e: Event) => {
            const event = <CustomEvent> e;
            const element = <Element> event.target;
            if (this.fieldDependencyAction === 'show' || this.fieldDependencyAction === 'hide') {
                element && this._updateVisibility(element, this._getFieldDependencyElementValues(element));
            }
        }

        private _updateVisibility(fieldDependencyElement: Element, value: string[]) {
            const isDependencyVisible = fieldDependencyElement.checkVisibility();
            if (fieldDependencyElement) {
                const isMatching: boolean = this._areValuesMatching(value, this._splitFieldValue(), this.fieldDependencyComparisonType)
                isDependencyVisible && isMatching ? this._show() : this._hide();
                if (this.fieldDependencyAction === 'show') {
                  isDependencyVisible && isMatching ? this._show() : this._hide();
                } else if (this.fieldDependencyAction === 'hide') {
                  isDependencyVisible && isMatching ? this._hide() : this._show();
                }
            }
        }

        public _show() {
            this.isFieldDependencyMatching = false;
            const event = new CustomEvent('form-control-dependency-show');
            this.dispatchEvent(event);
        }

        public _hide() {
            this.isFieldDependencyMatching = true;
            const event = new CustomEvent('form-control-dependency-hide');
            this.dispatchEvent(event);
        }
        private _getFieldDependencyElementValues(fieldDependencyElement: Element) {
           switch (true) {
              case fieldDependencyElement.tagName === 'SBK-SELECT':
                   return [(<SbkSelect> fieldDependencyElement).selectedOption?.value];
              case fieldDependencyElement.tagName === 'SBK-CHECKBOX': {
                  const element = <SbkCheckbox> fieldDependencyElement;
                  const checkedCheckboxes = Array.from(document.querySelectorAll(`sbk-checkbox[name='${element.name}'][checked]`))
                  return checkedCheckboxes.map((cb) => (cb as SbkCheckbox).value) }
              case fieldDependencyElement.tagName === 'SBK-RADIO': {
                  const element = <SbkRadio> fieldDependencyElement;
                  const checkedRadio = <SbkRadio> document.querySelector(`sbk-radio[name='${element.name}'][checked]`)
                  return checkedRadio ? [checkedRadio.value] : []
              }
              case fieldDependencyElement.tagName === 'SBK-RADIO-GROUP': {
                  const element = <SbkRadioGroup> fieldDependencyElement;
                  const checkedRadio = <SbkRadio> element.querySelector(`sbk-radio[checked]`)
                  return checkedRadio ? [checkedRadio.value] : []
              }
              case fieldDependencyElement.tagName === 'SBK-INPUT':
                   return [(<SbkInput> fieldDependencyElement).value];
              case fieldDependencyElement.tagName === 'SBK-TEXTAREA':
                   return [(<SbkTextarea> fieldDependencyElement).value];
              case fieldDependencyElement.tagName === 'SBK-DATEPICKER': {
                    return [(<SbkDatepicker> fieldDependencyElement).datepickerValue];
              }
              case fieldDependencyElement.tagName === 'SBK-REPEATABLE-CONTAINER': {
                    //const repeatableContainer = <SbkRepeatableContainer>fieldDependencyElement;
                    const values: string[] = [];

                    /*
                    repeatableContainer.fields.forEach((field) => {
                        const fieldElement = this.renderRoot.querySelector(`#${field.id}`) as HTMLElement;

                        if (fieldElement) {
                            const inputElements = fieldElement.querySelectorAll('sbk-input, sbk-select');
                            inputElements.forEach((input) => {
                                values.push((<HTMLInputElement>input).value);
                            });
                        }
                    }); */
                    return values;
                }

              default:
                   return [];
           }
        }

        private _splitFieldValue() {
            if (this.fieldDependencyComparisonType === 'range') {
                return [this.fieldDependencyValue]; // range as single string
            }
            return this.fieldDependencyValue ? this.fieldDependencyValue.split( "|" ) : [''];
        }

        private _areValuesMatching(formElementValues: string[], searchValues: string[], comparisonType: string) {
            if (typeof formElementValues[0] == 'undefined') formElementValues[0] = ''

            // check date for the last 3 years, back from current
            if (searchValues[0] === "REGEXYEAR") {
                const currentFullYear = new Date().getFullYear();
                for (let i = 0; i <= 2; i++) {
                    const result = formElementValues[0].match((currentFullYear - i).toString());
                    if (result != null && result.length > 0) {
                        return true
                    }
                }
                return false
            }
            // check date for future years
            if (searchValues[0] === "REGEXFUTURE") {
                const currentFullYear = new Date().getFullYear();
                return currentFullYear < parseInt(formElementValues[0].substr(formElementValues[0].length - 4));
            }
            if (comparisonType === 'getDay') {
                if (!formElementValues[0]) {
                    return false
                }
                const date = new Date(formElementValues[0])
                const days = searchValues.map(day => parseInt(day))
                return days.includes(date.getDay())
            }

            if (['lt', 'lte', 'gt', 'gte', 'eq'].includes(comparisonType)) {
                if (formElementValues.length !== 1 || searchValues.length !== 1) {
                    return false;
                }

                const formValue = parseFloat(formElementValues[0]);
                const searchValue = parseFloat(searchValues[0]);

                if (isNaN(formValue) || isNaN(searchValue)) {
                    return false;
                }

                switch (comparisonType) {
                    case 'lt':
                        return formValue < searchValue;
                    case 'lte':
                        return formValue <= searchValue;
                    case 'gt':
                        return formValue > searchValue;
                    case 'gte':
                        return formValue >= searchValue;
                    case 'eq':
                        return formValue === searchValue;
                    default:
                        return false;
                }
            }

            // range
            if (comparisonType === 'range') {
                if (formElementValues.length !== 1 || searchValues.length !== 1) {
                    return false;
                }

                const formValue = parseFloat(formElementValues[0]);
                const [from, to] = searchValues[0].split('-').map(value => parseFloat(value.trim()));

                if (isNaN(formValue) || isNaN(from) || isNaN(to)) {
                    return false;
                }

                return formValue >= from && formValue <= to;
            }

            const [smallArray, bigArray] =
                formElementValues.length < searchValues.length ? [formElementValues, searchValues] : [searchValues, formElementValues];
            return smallArray.some(value => bigArray.includes(value));
        }
    }
    return FormControlDependencyMixin as Constructor<FormControlDependencyMixinInterface> & T;
}
